import React, { useState, useEffect } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useQuery, useMutation } from '@tanstack/react-query';
import { employeeService } from '../../api/services/employeeService';
import { sewingReportService } from '../../api/services/sewingReportService';
import { SewingReportData, SewingReportFilter } from '@shared/types';
import { EmployeeType } from '@shared/types/Employee';
import { toast } from 'react-hot-toast';

export default function SewingReport() {
  const [employeeType, setEmployeeType] = useState<number>(EmployeeType.Sewer);
  const [selectedSewers, setSelectedSewers] = useState<Record<number, boolean>>({});
  const [reportData, setReportData] = useState<SewingReportData[] | null>(null);

  // Calculate default date range (last 30 days)
  const getDefaultDateRange = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      start: thirtyDaysAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0],
    };
  };

  const [filter, setFilter] = useState<SewingReportFilter>({
    ...getDefaultDateRange(),
    sewerIds: [],
    type: EmployeeType.Sewer,
    itemType: 0, // 0 = Both
  });

  // Fetch employees by type
  const { data: employeesData, isLoading: loadingEmployees } = useQuery({
    queryKey: ['employees', employeeType],
    queryFn: () => employeeService.getEmployeesByType(employeeType),
  });

  // Filter out "Discount" employee (not a real employee)
  const employees = employeesData?.filter(
    (emp) => emp.FirstName !== 'Discount' && emp.LastName !== 'Discount'
  );

  // Generate report mutation
  const generateReportMutation = useMutation({
    mutationFn: (reportFilter: SewingReportFilter) => sewingReportService.getReport(reportFilter),
    onSuccess: (data) => {
      setReportData(data);
      if (data.length === 0) {
        toast.success('Report generated (no data found for selected criteria)');
      } else {
        toast.success('Report generated successfully');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to generate report');
    },
  });

  // Update filter when employee type changes
  useEffect(() => {
    setFilter((prev) => ({ ...prev, type: employeeType }));
    setSelectedSewers({});
  }, [employeeType]);

  const handleCheckAll = () => {
    if (!employees) return;
    const newSelected: Record<number, boolean> = {};
    employees.forEach((emp) => {
      newSelected[emp.Id] = true;
    });
    setSelectedSewers(newSelected);
  };

  const handleUncheckAll = () => {
    setSelectedSewers({});
  };

  const handleGenerateReport = () => {
    const selectedIds = Object.keys(selectedSewers)
      .filter((id) => selectedSewers[parseInt(id)])
      .map((id) => parseInt(id));

    if (selectedIds.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    const reportFilter: SewingReportFilter = {
      ...filter,
      sewerIds: selectedIds,
    };

    generateReportMutation.mutate(reportFilter);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGoBack = () => {
    setReportData(null);
  };

  const calculateTotal = (sewer: SewingReportData): number => {
    return sewer.Items.reduce((sum, item) => {
      if (item.Amount < 0.01) {
        sewer.priceMissing = true;
      }
      return sum + (item.Amount || 0);
    }, 0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDateTime = (date: Date): string => {
    return new Date(date).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const employeeTypeName = employeeType === EmployeeType.Sewer ? 'Sewer' : 'Trimmer';

  if (reportData) {
    // Report View
    return (
      <MainLayout>
        <div className="px-4 py-6 sm:px-0">
          <div className="noprint text-right mb-4 space-x-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Print
            </button>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Go Back
            </button>
          </div>

          {reportData.map((sewer, index) => (
            <div key={index} className="sewer-section bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {sewer.FirstName} {sewer.LastName}
                </h2>
                <div className="text-right text-sm text-gray-600 dark:text-gray-400">
                  <p>
                    Showing from <strong>{filter.start}</strong> to <strong>{filter.end}</strong>
                  </p>
                  <p>ID Number: {sewer.SewerId}</p>
                  <p># of Items: {sewer.Items.length}</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Init.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date/Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Item No.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Part Num.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Code</th>
                      {employeeType !== EmployeeType.Trimmer && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Price</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sewer.Items.map((item, itemIndex) => (
                      <tr
                        key={itemIndex}
                        className={!item.CostCode ? 'bg-red-50 dark:bg-red-900/20' : ''}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-gray-100">
                          {itemIndex + 1}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {sewer.FirstName.charAt(0)}{sewer.LastName.charAt(0)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDateTime(item.CreateDate)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {item.OrderId}-{item.Ordinal}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {item.PartNumber}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                          {item.Description}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {item.Type}
                        </td>
                        <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 dark:text-gray-100">
                          {item.PriceCode}
                        </td>
                        {employeeType !== EmployeeType.Trimmer && (
                          <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 dark:text-gray-100">
                            {item.Amount > 0 ? formatCurrency(item.Amount) : ''}
                          </td>
                        )}
                      </tr>
                    ))}
                    {employeeType !== EmployeeType.Trimmer && (
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={9} className="px-3 py-2 text-right text-sm font-bold text-gray-900 dark:text-gray-100">
                          Total for {sewer.FirstName} {sewer.LastName}: {formatCurrency(calculateTotal(sewer))}
                          {sewer.priceMissing && <span className="ml-2">+ ____________ = _____________</span>}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </MainLayout>
    );
  }

  // Filter Form View
  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {employeeTypeName} Report
              </h2>
              <select
                value={employeeType}
                onChange={(e) => setEmployeeType(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value={EmployeeType.Sewer}>Sewer</option>
                <option value={EmployeeType.Trimmer}>Trimmer</option>
              </select>
            </div>
          </div>

          <div className="p-6">
            {/* Date Range */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Date range</h3>
              <div className="flex gap-4 items-center">
                <input
                  type="date"
                  value={filter.start}
                  onChange={(e) => setFilter({ ...filter, start: e.target.value })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                />
                <span className="text-gray-500 dark:text-gray-400">to</span>
                <input
                  type="date"
                  value={filter.end}
                  onChange={(e) => setFilter({ ...filter, end: e.target.value })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Employee Selection */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{employeeTypeName}</h3>
                <div className="space-x-2">
                  <button
                    onClick={handleCheckAll}
                    className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Check all
                  </button>
                  <button
                    onClick={handleUncheckAll}
                    className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Uncheck all
                  </button>
                </div>
              </div>
              {loadingEmployees ? (
                <p className="text-gray-500 dark:text-gray-400">Loading employees...</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {employees?.map((emp) => (
                    <label key={emp.Id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!selectedSewers[emp.Id]}
                        onChange={(e) =>
                          setSelectedSewers({
                            ...selectedSewers,
                            [emp.Id]: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {emp.FirstName} {emp.LastName}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Item Type */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Items</h3>
              <div className="flex gap-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="itemType"
                    value="0"
                    checked={filter.itemType === 0}
                    onChange={() => setFilter({ ...filter, itemType: 0 })}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Both</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="itemType"
                    value="1"
                    checked={filter.itemType === 1}
                    onChange={() => setFilter({ ...filter, itemType: 1 })}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Seat Covers</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="itemType"
                    value="2"
                    checked={filter.itemType === 2}
                    onChange={() => setFilter({ ...filter, itemType: 2 })}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">BRA/Other</span>
                </label>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleGenerateReport}
                disabled={generateReportMutation.isPending}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {generateReportMutation.isPending ? 'Generating...' : 'Generate'}
              </button>
              {generateReportMutation.isPending && (
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .noprint {
            display: none !important;
          }
          .sewer-section {
            page-break-inside: avoid;
            page-break-after: always;
          }
        }
      `}</style>
    </MainLayout>
  );
}
